import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  Image,
  Button,
  Flex,
  Container,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  setUser,
  useLoginMutation,
  useAppDispatch,
  useResetPasswordMutation,
  setDashBoardCards,
  setJustLoggedIn,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { LoginTruck } from 'assets';
import logo from 'assets/svg/logo-dois.svg';
import SimpleInput from 'components/SimpleInput';
import useAnalyticsEvent from 'hooks/useAnalyticsEvent';
import { useMfaVerifyMutation } from 'setup/authOverrides';
import { isMfaRequiredQueryError } from 'setup/authOverrides';
import { MfaPendingResponse } from 'setup/mfaTypes';
import { GA_LOGIN } from 'utils/constants';
import { getPostLoginPath } from 'utils/hasPortalPermission';
import { isValidCPF } from 'utils/validations';
import * as z from 'zod';
import { FgtPwModal } from './components/forgotPwModal';
import MfaSetup from './components/MfaSetup';
import MfaVerify from './components/MfaVerify';
import PwdRequirements from './components/PwdRequirements';
import { SolicitacaoAcessoModal } from './components/solicitacaoAcessoModal';
import { useMfaSession } from './hooks/useMfaSession';

const loginSchema = z.object({
  username: z
    .string({ required_error: 'CPF não pode ser vazio' })
    .min(14, { message: 'CPF não pode ser vazio' })
    .refine(value => !value.includes('_'), 'CPF vazio ou incompleto')
    .refine(value => isValidCPF(value), 'CPF inválido'),

  password: z.string().nonempty('Senha não pode ser vazia'),
});

const pwdSchema = z
  .object({
    newPwd: z
      .string()
      .min(8, 'Senha precisa ter no mínimo 8 dígitos')
      .nonempty('Senha não pode ser vazia')
      .refine(
        value => /[a-z]/.test(value),
        'Senha precisa ter pelo menos 1 letra minúscula',
      )
      .refine(
        value => /[A-Z]/.test(value),
        'Senha precisa ter pelo menos 1 letra maiúscula',
      )
      .refine(
        value => /[0-9]/.test(value),
        'Senha precisa ter pelo menos um número',
      )
      .refine(
        value => /\W/.test(value),
        'Senha precisa ter pelo menos 1 carácter especial',
      ),
    confirmNewPwd: z.string(),
  })
  .refine(data => data.confirmNewPwd === data.newPwd, {
    message: 'Senhas não são iguais',
    path: ['confirmNewPwd'],
  });

const Login: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAccessOpen,
    onOpen: onAccessOpen,
    onClose: onAccessClose,
  } = useDisclosure();
  const [login, { isLoading: isLoadingLogin, isError }] = useLoginMutation();
  const [resetPwd, { isLoading: isLoadingResetPwd }] =
    useResetPasswordMutation();
  const [mfaVerify, { isLoading: isLoadingMfaVerify }] = useMfaVerifyMutation();
  const { saveSession, clearSession } = useMfaSession();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const page = useAppSelector(state => state.pagesSlicer.lastPage);

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_LOGIN,
  });

  const [currentUsername, setCurrentUsername] = useState<string | undefined>();
  const [currentPassword, setCurrentPassword] = useState<string | undefined>();
  const [mfaSession, setMfaSession] = useState<MfaPendingResponse | null>(null);
  const [mfaError, setMfaError] = useState<string>();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useForm<{
    username: string;
    password: string;
    newPwd?: string;
    confirmNewPwd?: string;
  }>({
    resolver: zodResolver(currentUsername ? pwdSchema : loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const newPwd = watch('newPwd');

  const completeLogin = useCallback(
    (user: User) => {
      dispatch(setUser({ user }));
      dispatch(setDashBoardCards(null));
      dispatch(setJustLoggedIn(true));

      analyticsEvent(
        `${user.currentGroup?.currentGroupName} | Efetuou o login`,
      );

      const postLoginPath = getPostLoginPath(user.permissions, page);
      if (/^https?:\/\//i.test(postLoginPath)) {
        window.location.assign(postLoginPath);
      } else {
        navigate(postLoginPath);
      }
    },
    [analyticsEvent, dispatch, navigate, page],
  );

  const handleBackToLogin = useCallback(() => {
    clearSession();
    setMfaSession(null);
    setMfaError(undefined);
  }, [clearSession]);

  const handleMfaVerify = useCallback(
    async (code: string, setupKey?: string) => {
      if (!mfaSession?.mfaToken) return;

      try {
        setMfaError(undefined);
        const user = await mfaVerify({
          mfaToken: mfaSession.mfaToken,
          code,
          setupKey,
        }).unwrap();

        clearSession();
        setMfaSession(null);

        if (user.provisionalPassword === 'S' || user.firstAccess === 'S') {
          setCurrentUsername(user.cpf ?? undefined);
          setCurrentPassword('');
          throw Error('new pwd');
        }

        completeLogin(user);
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'new pwd') {
          return;
        }

        setMfaError('Código inválido ou sessão expirada.');
      }
    },
    [clearSession, completeLogin, mfaSession, mfaVerify],
  );

  const onSubmit = useCallback(
    async (data: {
      username?: string;
      password?: string;
      newPwd?: string;
      confirmNewPwd?: string;
    }) => {
      try {
        let user = {} as User;
        if (currentUsername && currentPassword) {
          user = await resetPwd({
            username: currentUsername || '',
            pwd: currentPassword || '',
            newPwd: data.newPwd || '',
          }).unwrap();
        } else {
          user = await login({
            username: data.username || '',
            password: data.password || '',
          }).unwrap();
        }

        if (user.provisionalPassword === 'S' || user.firstAccess === 'S')
          throw Error('new pwd');

        completeLogin(user);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (isMfaRequiredQueryError(error)) {
          saveSession(error.data);
          setMfaSession(error.data);
          setMfaError(undefined);
          setCurrentUsername(undefined);
          setCurrentPassword(undefined);
          return;
        }

        if (error.message === 'new pwd') {
          setCurrentUsername(data.username);
          setCurrentPassword(data.password);
        }
      }
    },
    [
      completeLogin,
      currentUsername,
      currentPassword,
      login,
      page,
      resetPwd,
      saveSession,
    ],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box flex={1} backgroundColor="white">
        <FgtPwModal isOpen={isOpen} onClose={onClose} />
        <SolicitacaoAcessoModal
          isOpen={isAccessOpen}
          onClose={onAccessClose}
        />
        <Flex minH="100vh">
          <Box
            display={{ base: 'none', md: 'inline' }}
            w="50%"
            maxW="912px"
            pl="79px"
            pr="95px"
            pt="100px"
            backgroundSize="cover"
            backgroundImage={`url(${LoginTruck})`}
          >
            <Text fontWeight="700" fontSize="40px">
              Soluções que facilitam a gestão do negócio.
            </Text>
            <Text mt="18px">
              Os planos permitem que os clientes estejam no controle de suas
              frotas e que seus negócios contem ainda mais com tecnologia e
              inovação.
            </Text>
          </Box>
          <Flex
            pt={currentUsername && currentPassword ? '0px' : '150px'}
            align={currentUsername && currentPassword ? 'center' : 'start'}
            flex={1}
          >
            <Container maxW="430px">
              <Flex justifyContent={{ base: 'center', md: 'start' }}>
                <Image alt="" src={logo} mb="32px" h="30%" w="30%" />
              </Flex>

              {mfaSession ? (
                mfaSession.setupRequired ? (
                  <MfaSetup
                    mfaToken={mfaSession.mfaToken}
                    isVerifying={isLoadingMfaVerify}
                    verifyError={mfaError}
                    onVerify={handleMfaVerify}
                    onBack={handleBackToLogin}
                  />
                ) : (
                  <MfaVerify
                    isLoading={isLoadingMfaVerify}
                    errorMessage={mfaError}
                    onSubmit={code => handleMfaVerify(code)}
                    onBack={handleBackToLogin}
                  />
                )
              ) : currentUsername && currentPassword ? (
                <>
                  <Text
                    mb="32px"
                    fontWeight="500"
                    fontSize="22px"
                    color="black"
                  >
                    Crie uma nova senha
                  </Text>

                  <SimpleInput
                    label="Nova senha"
                    {...register('newPwd')}
                    id="newPwd"
                    error={errors?.newPwd?.message?.toString()}
                    bg="transparent"
                    placeholder="Informe sua nova senha"
                    type="password"
                    mb="18px"
                  />

                  <SimpleInput
                    label="Confirme nova senha"
                    {...register('confirmNewPwd')}
                    id="confirmNewPwd"
                    error={errors?.confirmNewPwd?.message?.toString()}
                    bg="transparent"
                    placeholder="Confirme sua nova senha"
                    type="password"
                    mb="18px"
                  />

                  <PwdRequirements password={newPwd} mb="18px" />
                </>
              ) : (
                <>
                  <Text fontWeight="500" fontSize="22px" color="black">
                    Bem vindo
                  </Text>

                  <Text fontSize="sm" color="text.300">
                    Insira as informações abaixo para logar
                  </Text>

                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <SimpleInput
                        id="username"
                        error={errors?.username?.message?.toString()}
                        bg="transparent"
                        mt="18px"
                        mb="18px"
                        placeholder="Digite seu CPF"
                        autoComplete="on"
                        handleFill={value => field.onChange(value ?? '')}
                        value={field.value}
                        type="cpf"
                      />
                    )}
                  />

                  <SimpleInput
                    {...register('password')}
                    id="password"
                    error={errors?.password?.message?.toString()}
                    bg="transparent"
                    current-password="true"
                    autoComplete="on"
                    placeholder="Digite sua senha"
                    type="password"
                  />

                  {isError && (
                    <Text mt={2} color="red.200">
                      Login ou senha inválidos
                    </Text>
                  )}

                  <Flex mt="18px" mb="36px">
                    <Button
                      textColor="ocean.200"
                      variant="link"
                      _hover={{ opacity: 0.5 }}
                      onClick={onOpen}
                      as="button"
                      marginInlineStart="auto"
                    >
                      Esqueci a senha
                    </Button>
                  </Flex>
                </>
              )}

              <Button
                w="100%"
                type="submit"
                isLoading={isLoadingLogin || isLoadingResetPwd || isLoadingMfaVerify}
                display={mfaSession ? 'none' : 'inline-flex'}
              >
                Entrar
              </Button>

              <Flex
                mb="24px"
                mt="18px"
                w="100%"
                justifyContent="center"
                display={mfaSession ? 'none' : 'flex'}
              >
                <Text color="text.300" fontSize="xs">
                  Não tem acesso?
                </Text>
                <Link
                  as="button"
                  type="button"
                  textDecor="underline"
                  ml="4px"
                  fontSize="xs"
                  color="ocean.200"
                  onClick={onAccessOpen}
                >
                  Clique e faça a solicitação
                </Link>
              </Flex>
            </Container>
          </Flex>
        </Flex>
      </Box>
    </form>
  );
};

export default Login;
